import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../shared/components/toast/useToast';
import { outreachApi } from '../api/outreachApi';
import type {
  CreateOutreachSendPayload,
  CreateOutreachTemplatePayload,
  OutreachContactStage,
  OutreachEngagementModel,
  OutreachTemplateQuery,
  UpdateOutreachTemplatePayload,
} from '../types';

const KEY = 'outreach';

/** Nest validation errors arrive as `message: string[]`; take the first line. */
const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

/** ApiFetch has already toasted anything that reached the server; only cover the rest. */
const useErrorToast = () => {
  const { toast } = useToast();
  return (fallback: string) => (err: unknown) => {
    if (!(err as { toasted?: boolean })?.toasted) toast.error(errMsg(err, fallback));
  };
};

export const useOutreachTemplates = (query: OutreachTemplateQuery = {}) =>
  useQuery({
    queryKey: [KEY, 'templates', query],
    queryFn: () => outreachApi.listTemplates(query),
    staleTime: 30 * 1000,
  });

export const useCreateOutreachTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (payload: CreateOutreachTemplatePayload) => outreachApi.createTemplate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'templates'] });
      toast.success('Template saved');
    },
    onError: onError('Failed to save template'),
  });
};

export const useUpdateOutreachTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateOutreachTemplatePayload }) =>
      outreachApi.updateTemplate(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'templates'] });
      toast.success('Template updated');
    },
    onError: onError('Failed to update template'),
  });
};

export const useOutreachBrandCard = (contactId: string | undefined) =>
  useQuery({
    queryKey: [KEY, 'brand-card', contactId],
    queryFn: () => outreachApi.getBrandCard(contactId as string),
    enabled: !!contactId,
  });

export const useGenerateBrandCard = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (contactId: string) => outreachApi.generateBrandCard(contactId),
    onSuccess: (card, contactId) => {
      queryClient.setQueryData([KEY, 'brand-card', contactId], card);
      toast.success('Brand card generated');
    },
    onError: onError('Failed to generate brand card'),
  });
};

/** No cache entry — every draft is a fresh, throwaway suggestion. */
export const useGenerateOutreachDraft = () => {
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({
      contactId,
      engagementModel,
    }: {
      contactId: string;
      engagementModel: OutreachEngagementModel;
    }) => outreachApi.generateDraft(contactId, engagementModel),
    onError: onError('Failed to generate a draft'),
  });
};

export const useOutreachSends = (contactId: string | undefined) =>
  useQuery({
    queryKey: [KEY, 'sends', contactId],
    queryFn: () => outreachApi.listSends(contactId as string),
    enabled: !!contactId,
  });

export const useLogOutreachSend = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ contactId, payload }: { contactId: string; payload: CreateOutreachSendPayload }) =>
      outreachApi.logSend(contactId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'sends', variables.contactId] });
      // A send resets the contact's follow-up clock.
      queryClient.invalidateQueries({ queryKey: [KEY, 'due'] });
      toast.success('Send recorded');
    },
    onError: onError('Failed to record the send'),
  });
};

export const useOutreachDue = () =>
  useQuery({
    queryKey: [KEY, 'due'],
    queryFn: () => outreachApi.listDue(),
    staleTime: 30 * 1000,
  });

export const useStageCadences = () =>
  useQuery({
    queryKey: [KEY, 'cadence', 'stages'],
    queryFn: () => outreachApi.listStageCadences(),
  });

export const useSetStageCadence = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ stage, intervalDays }: { stage: OutreachContactStage; intervalDays: number }) =>
      outreachApi.setStageCadence(stage, intervalDays),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'cadence', 'stages'] });
      queryClient.invalidateQueries({ queryKey: [KEY, 'due'] });
      toast.success('Cadence updated');
    },
    onError: onError('Failed to update cadence'),
  });
};

export const useContactCadence = (contactId: string | undefined) =>
  useQuery({
    queryKey: [KEY, 'cadence', 'contact', contactId],
    queryFn: () => outreachApi.getContactCadence(contactId as string),
    enabled: !!contactId,
  });

export const useSetContactCadence = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ contactId, intervalDays }: { contactId: string; intervalDays: number | null }) =>
      outreachApi.setContactCadence(contactId, intervalDays),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'cadence', 'contact', variables.contactId] });
      queryClient.invalidateQueries({ queryKey: [KEY, 'due'] });
      toast.success(variables.intervalDays === null ? 'Override cleared' : 'Override saved');
    },
    onError: onError('Failed to update the follow-up override'),
  });
};
